/**
 * Public SDK surface. Assembles the kernel (`core/`) and the config-activated
 * modules (`modules/`) into the object attached to `window.KueezSDK`.
 */

import {
  init as runCoreInit,
  isInitStarted,
  _resetInitForTests,
} from './core/init';
import {
  getScriptAttrs,
  hasScriptLoadHandler,
  setPreInitOption,
  _resetOptionsForTests,
  type ConfigStore,
  type InitOptions,
} from './core/options';
import {
  getSdkState,
  getSdkStateOrNull,
  _resetSdkStateForTests,
} from './core/state';
import type { SdkConfig, AdAccountConfig } from './core/config/types';
import { SDK_VERSION, TRACKER_BASE_URLS } from './core/config/constants';
import { track } from './core/analytics/track';
import { setDebug, warn } from './util/log';
import { on, SdkEvent, _resetBus } from './core/events/bus';
import { AwaitFor, awaitFor, _resetAwaiter } from './core/events/awaiter';
import {
  installEnabledModules,
  requireModule,
  getInstalledModule,
  isModuleInstalled,
  _resetModulesForTests,
} from './modules/registry';
import type { SdkPost } from './modules/pool/types';
import { _resetPixelsForTests } from './modules/pixels/module';
import { _resetMbpForTests } from './modules/mbp/api';
import { _resetTimeOnSiteForTests } from './core/analytics/timeOnSite';
import { _resetInMobiForTests } from './modules/privacy/inmobi';
import { _resetUaCache } from './core/ua/parse';
import {
  _resetPageviewIdForTests,
  _clearSdkSessionStorageForTests,
  pageviewId,
  random64BitHex,
  readPersistentSessionId,
} from './core/identity';
import { _resetLoaderForTests, loaderApi, applyLoaderScriptAttr } from './core/loader';
import { _resetKzqForTests, armKzqEmergencyFlush } from './core/kzq';
import { dispatchToTrackers } from './core/analytics/transport';
import { makeFallbackIds } from './core/state';
import { buildEmergencyCommonParams } from './core/analytics/commonParams';
import { isBotTraffic } from './core/ua/bot';
import { MBP_EVENTS } from './modules/mbp/events';
import { emergencyPredictionParams } from './modules/mbp/observers';
import type { GptSlotRenderEndedEvent } from './modules/mbp/types';
import type { ResolvedOptions } from './core/options';
import type { ConsentInit } from './core/consent/port';

export type { InitOptions, InitPixelOption, ResolvedOptions } from './core/options';
export type { LoaderConfigureOptions } from './core/loader';
export type { SdkConfig, AdAccountConfig } from './core/config/types';
export type { SdkContext } from './core/context';
export type { EventParams, EventParamValue } from './core/analytics/track';
export type { EventPayloads, EcomClickoutSignal, Unsubscribe } from './core/events/bus';
export type { ClickoutPayload } from './modules/ecom/events';
export type { ModuleApiMap } from './modules/registry';
export type { SdkModuleDefinition, SdkModuleId } from './modules/types';
export type { SdkPost, SdkWebsite, SdkPostItem } from './modules/pool/types';

export const version: string = SDK_VERSION;

/* -------------------------------------------------------------------------- */
/* init + config                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Pre-install emergency sender for `window.kzq`: a page hidden or abandoned
 * during the config fetch still reports its buffered ad events. Only ad
 * requests and `/p` renders go out — never `session_init`.
 *
 * Session stays READY-gated on purpose. `sdk.js` is preloaded and inits from
 * the head stub, so an early-exit flush can run before yoto's analytics bundle
 * has even loaded; firing a session there invents sessions yoto never counts
 * (the +8% session overshoot on 2026-08-02). Ad events are safe: GPT only
 * fires them after the page's ad stack is up, by which time yoto is past
 * `initMbpAnalytics` too.
 *
 * Renders are priced from the slot's own `hb_pb` targeting — no config needed —
 * with the config-free backfill group (ADX). The user id
 * stays zeroed: minting one here would consentlessly bypass the identity rules.
 * Every config-free common param travels along (`buildEmergencyCommonParams`) —
 * a missing `abtv` would land the events outside the ab_test_value segments the
 * gap is measured by. Init-option trackers win (hometalk points at stage);
 * otherwise the hardcoded production fanout.
 */
function armPreInstallKzqFlush(options: ResolvedOptions): void {
  const trackers = options.trackers.length > 0 ? options.trackers : [...TRACKER_BASE_URLS];
  armKzqEmergencyFlush((events) => {
    if (isBotTraffic() || events.length === 0) return;
    const common = buildEmergencyCommonParams(options, {
      userId: makeFallbackIds().userId,
      // Prefer the tab's real sid when READY already minted one on a prior
      // page; otherwise a random non-zeroed id so rescued ad events still group.
      sessionId: readPersistentSessionId() ?? random64BitHex(),
      pageviewId: pageviewId(),
    });
    for (const entry of events) {
      if (entry.source !== 'gpt') continue;
      if (entry.name === 'slotRequested') {
        let path = '';
        try {
          path =
            (entry.event as { slot?: { getAdUnitPath?(): string } }).slot?.getAdUnitPath?.() ?? '';
        } catch {
          /* keep empty */
        }
        dispatchToTrackers(trackers, {
          ...common,
          type: MBP_EVENTS.adRequest,
          info: path,
          _: Date.now(),
        });
      } else if (entry.name === 'slotRenderEnded') {
        dispatchToTrackers(
          trackers,
          {
            ...common,
            ...emergencyPredictionParams(entry.event as GptSlotRenderEndedEvent),
            _: Date.now(),
          },
          '/p',
        );
      }
    }
  });
}

export function init(opts: InitOptions = {}): void {
  runCoreInit(opts, {
    installModules: (ctx, phase) =>
      installEnabledModules(ctx, phase === 'consent' ? ['privacy'] : undefined),
    onOptionsResolved: armPreInstallKzqFlush,
    sdkVersion: version,
  });
}

export function config(key: 'consent', value: ConsentInit): void;
export function config<K extends keyof ConfigStore>(key: K, value: ConfigStore[K]): void;
export function config(key: keyof ConfigStore, value: unknown): void {
  setPreInitOption(key, value as ConfigStore[typeof key]);

  if (key === 'debug') setDebug(value === true);

  const state = getSdkStateOrNull();
  if (!state) return;
  if (key === 'consent') {
    state.engine.setOverride(value as ConsentInit);
  } else if (key === 'mediaBuyingEnabled') {
    state.options.mediaBuyingEnabled = value !== false;
  } else if (key === 'apiBaseUrl' && typeof value === 'string' && value.trim()) {
    state.options.apiBaseUrl = value.trim();
  } else if (key === 'includeDrafts') {
    state.options.includeDrafts = value === true;
  } else if (key === 'debug') {
    state.options.debug = value === true;
  }
}

/**
 * Every script load starts the SDK. There is no marker to forget, which is what
 * made the old opt-in fail silently: an embed that lost its `id` ran nothing and
 * said nothing.
 *
 * `onload` on the tag is the single opt-out — the host took the load callback, so
 * it owns the `init()` call. Skipping is announced, because from here it is
 * indistinguishable from a handler added for some unrelated reason, and a silent
 * skip is the failure this change exists to remove.
 */
export function maybeAutoInitFromScriptAttrs(): void {
  if (isInitStarted()) return;
  if (hasScriptLoadHandler()) {
    if (typeof console !== 'undefined') {
      console.warn(
        '[kueez-sdk] script tag has an onload handler — skipping auto-init. Call KueezSDK.init() from it.',
      );
    }
    return;
  }
  init({});
}

/** Apply `data-kz-loader` captured from the script tag (before or with auto-init). */
export function applyLoaderFromScriptAttrs(): void {
  applyLoaderScriptAttr(getScriptAttrs().loader);
}

/** Normalized website config from the last successful `init()`. */
export function getConfig(): SdkConfig {
  return getSdkState().config;
}

/** Ad account matched from `sourceMedium` (init or page UTMs), or null. */
export function getActiveAdAccount(): AdAccountConfig | null {
  return getSdkState().activeAdAccount;
}

/* -------------------------------------------------------------------------- */
/* Loader                                                                      */
/* -------------------------------------------------------------------------- */

export const loader = loaderApi;

/* -------------------------------------------------------------------------- */
/* Analytics                                                                   */
/* -------------------------------------------------------------------------- */

export { track } from './core/analytics/track';

/* -------------------------------------------------------------------------- */
/* Events                                                                      */
/* -------------------------------------------------------------------------- */

export const events = {
  on,
  SdkEvent,
};

export function ready(cb: () => void): void {
  awaitFor(AwaitFor.READY, () => cb());
}

export function whenContentReady(cb: (post: SdkPost | null) => void): void {
  // CONTENT completes with the active post (or null); narrow at this boundary.
  awaitFor(AwaitFor.CONTENT, (value) => cb((value as SdkPost | null) ?? null));
}

export function awaitContent(): Promise<SdkPost | null> {
  return new Promise((resolve) => {
    whenContentReady(resolve);
  });
}

/* -------------------------------------------------------------------------- */
/* Modules                                                                     */
/* -------------------------------------------------------------------------- */

export const modules = {
  get: requireModule,
  isInstalled: isModuleInstalled,
};

/* -------------------------------------------------------------------------- */
/* Consent + privacy                                                           */
/* -------------------------------------------------------------------------- */

export const consent = {
  status(): 'unknown' | 'granted' | 'denied' {
    return getSdkStateOrNull()?.engine.getStatus() ?? 'unknown';
  },
  set(status: 'granted' | 'denied'): void {
    const state = getSdkStateOrNull();
    if (!state) {
      warn('consent.set() called before init resolved');
      return;
    }
    state.engine.setOverride(status);
  },
  onChange(cb: (status: 'granted' | 'denied') => void): void {
    on(SdkEvent.CONSENT_CHANGED, ({ status }) => {
      try {
        cb(status);
      } catch (err) {
        warn('consent.onChange listener threw', err);
      }
    });
  },
};

export const privacy = {
  optOut(): void {
    const state = getSdkStateOrNull();
    if (!state) {
      warn('privacy.optOut() called before init resolved');
      return;
    }
    state.privacy.optOut();
  },
  optIn(): void {
    const state = getSdkStateOrNull();
    if (!state) {
      warn('privacy.optIn() called before init resolved');
      return;
    }
    state.privacy.optIn();
  },
  status(): 'opted-in' | 'opted-out' {
    return getSdkStateOrNull()?.privacy.status() ?? 'opted-in';
  },
};

/* -------------------------------------------------------------------------- */
/* Test hooks                                                                  */
/* -------------------------------------------------------------------------- */

export function _resetForTests(): void {
  _resetInitForTests();
  _resetSdkStateForTests();
  _resetOptionsForTests();
  _resetAwaiter();
  _resetBus();
  _resetModulesForTests();
  _resetPixelsForTests();
  _resetMbpForTests();
  _resetTimeOnSiteForTests();
  _resetInMobiForTests();
  _resetUaCache();
  _resetPageviewIdForTests();
  _clearSdkSessionStorageForTests();
  _resetLoaderForTests();
  _resetKzqForTests();
}

/* Module accessor helpers used by index.ts to define `KueezSDK.pool` etc. */
export { getInstalledModule, requireModule };
